import { ReadonlyVec2, mat4, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from 'notistack';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { WindowLevel } from '~/domain/patient/windowLevel';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { ContourOperations } from '~/engine/util/algorithms/sdf/ContourOperations';
import { DoseMatrix } from '../../domain/patient/doseMatrix';
import { Image3D } from '../../domain/patient/image3d';
import { StructureSet } from '../../domain/patient/structureset';
import { Transformation } from '../util/geometry/Transformation';
import { PanZoom } from '../view/PanZoom';
import { ViewManager } from '../view/ViewManager';
import { VolumeViewUpdateEvent } from '../view/events/VolumeViewUpdateEvent';
import { LayerBit } from '../view/layers/LayerBit';
import { EnvironmentObjects } from './EnvironmentObjects';
import { PointOfInterest } from './PointOfInterest';
import { EnvironmentObject } from './common/EnvironmentObject';
import { DoseDistribution } from './dose/DoseDistribution';
import { ImageObject } from './image/ImageObject';
import { StructureSetObject } from './structure/StructureSetObject';

export type EnvironmentObjectIds = {
    primaryImage: {
        seriesInstanceUID: string | undefined;
        studyInstanceUID: string | undefined;
    };
    blendImage: {
        seriesInstanceUID: string | undefined;
        studyInstanceUID: string | undefined;
    };
    structureSet: {
        sopInstanceUID: string | undefined;
    };
    dose: {
        sopInstanceUID: string | undefined;
    };
};

/**
 * {@link Environment} is a storage class, storing the state of the objects that are currently being viewed.\
 * Environment also stores the common viewing parameters shared between the views.
 */
export class Environment {
    private environmentObjects = new EnvironmentObjects();

    private readonly _panZoom: PanZoom = new PanZoom();

    public get panZoom(): PanZoom {
        return this._panZoom;
    }

    private readonly _planeIntersectionPoint: PointOfInterest =
        new PointOfInterest();

    public get planeIntersectionPoint(): PointOfInterest {
        return this._planeIntersectionPoint;
    }

    public get eventTarget(): EventTarget {
        return this._eventTarget;
    }

    // save changes to domain data model
    public saveChanges() {
        this.environmentObjects.structureSet?.saveChanges();
    }

    public constructor(private readonly _eventTarget: EventTarget) {}

    public setStructureSet(ss?: StructureSet): EnvironmentObjects | undefined {
        const { structureSet: currentSS, dose: currentDose } =
            this.environmentObjects;

        if (!ss) {
            this.removeStructureSetInternal();
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
            return this.environmentObjects;
        }

        if (!ss.image) {
            enqueueSnackbar(
                `Structure set ${ss.structureSetLabel} is not connected to an image.`,
                { variant: 'error' }
            );
            return;
        }

        if (currentSS?.parent === ss) {
            return this.environmentObjects;
        }

        if (currentSS) this.removeStructureSetInternal();
        if (currentDose) this.removeDoseInternal();

        this.setImage(ss.image);

        const newStructureSet = new StructureSetObject(ss);
        this.environmentObjects.structureSet = newStructureSet;

        this._currentStructureSetPropertyChangedCallback =
            this.notifyStructureSetPropertyChangedListeners.bind(this);
        newStructureSet.addListener(
            this._currentStructureSetPropertyChangedCallback
        );

        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );

        for (const roi of newStructureSet.structures) {
            if (roi.renderErrors) {
                enqueueSnackbar(
                    `Error rendering structure ${roi.name}: ${roi.renderErrors}`,
                    { variant: 'error' }
                );
            }
        }

        return this.environmentObjects;
    }

    private _currentStructureSetPropertyChangedCallback:
        | ((arg0: StructureSetObject) => void)
        | undefined = undefined;

    private notifyStructureSetPropertyChangedListeners(
        _ss: StructureSetObject
    ) {
        this._eventTarget.dispatchEvent(
            new Event('structure_set_property_changed')
        );
    }

    public setDose(dose: DoseMatrix | undefined) {
        if (this.environmentObjects.dose) {
            if (this.environmentObjects.dose.parent === dose) {
                return this.environmentObjects;
            }
            this.removeDoseInternal();
        }
        if (dose) {
            if (!dose.structureSet) {
                enqueueSnackbar(
                    `Dose ${dose.dicomUid} is not connected to a structure set.`,
                    { variant: 'error' }
                );
                return this.environmentObjects;
            }
            this.setStructureSet(dose.structureSet);
            this.environmentObjects.dose = new DoseDistribution(dose);
        }
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.dose,
                LayerBit.DOSE_LAYER_BIT,
                true
            )
        );
        return this.environmentObjects;
    }

    public updateSelectedDoseRange(range: ReadonlyVec2) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.selectedDoseRange = range;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.dose,
                    LayerBit.DOSE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public updateDoseAlphaLevelOpacity(opacity: number) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.alphaLevelOpacity = opacity;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.dose,
                    LayerBit.DOSE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public updateDoseVisibility(visible: boolean) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.isVisible = visible;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.dose,
                    LayerBit.DOSE_LAYER_BIT,
                    true
                )
            );
        }
    }

    public setImage(image: Image3D | undefined) {
        if (this.environmentObjects.primaryImage) {
            if (this.environmentObjects.primaryImage.parent === image) {
                return;
            }
            this.removeImagesInternal();
        }
        if (this.environmentObjects.dose) {
            this.removeDoseInternal();
        }
        if (this.environmentObjects.structureSet) {
            this.removeStructureSetInternal();
        }
        if (image) {
            this.environmentObjects = new EnvironmentObjects();
            this.environmentObjects.primaryImage = ImageObject.fromImage3D(
                image,
                image.firstSlice
            );
            ViewManager.resetViews();
        } else {
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.primaryImage,
                    LayerBit.ALL_LAYERS,
                    true
                )
            );
        }
        this._eventTarget.dispatchEvent(new Event('windowlevel_changed'));
        return this.environmentObjects;
    }

    private removeImagesInternal() {
        if (this.environmentObjects.blendImage) {
            this.removeObject(this.environmentObjects.blendImage);
            this.environmentObjects.blendImage = undefined;
        }
        if (this.environmentObjects.primaryImage) {
            this.removeObject(this.environmentObjects.primaryImage);
            this.environmentObjects.primaryImage = undefined;
        }
    }

    private removeEnvironmentBlendImageInternal() {
        if (this.environmentObjects.blendImage) {
            this.removeObject(this.environmentObjects.blendImage);
            this.environmentObjects.blendImage = undefined;
        }
    }

    private removeDoseInternal() {
        if (this.environmentObjects.dose) {
            this.removeObject(this.environmentObjects.dose);
            this.environmentObjects.dose = undefined;
        }
    }

    private removeStructureSetInternal() {
        if (this.environmentObjects.structureSet) {
            const ss = this.environmentObjects.structureSet;
            try {
                ss.saveChanges();
            } catch (e) {
                enqueueSnackbar(
                    `Error saving structure set changes: ${ss.displayName}, ${e}.`,
                    { variant: 'error' }
                );
            }
            this.removeObject(ss);
            if (this._currentStructureSetPropertyChangedCallback) {
                ss.removeListener(
                    this._currentStructureSetPropertyChangedCallback
                );
            }
            this.setSelectedStructure(undefined);
            this.environmentObjects.structureSet = undefined;
        }
    }

    public deleteRoi(roiNumber: number) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.parent.deleteRoi(roiNumber);
            this.environmentObjects.structureSet.deleteRoi(roiNumber);
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public clearContourOnSlice(
        structureName: string,
        planeCoordinates: number
    ) {
        if (this.environmentObjects.structureSet) {
            const imgObject = this.environmentObjects.primaryImage;
            this.environmentObjects.structureSet.clearContoursOnSlice(
                structureName,
                planeCoordinates,
                imgObject
            );
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public clearAllContours(structureName: string) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.clearAllContours(
                structureName
            );
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public keepEveryNthSlice(
        roiNumber: number,
        keepEveryNthSliceValue: number
    ) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.keepEveryNthSlice(
                roiNumber,
                keepEveryNthSliceValue
            );
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public undoContouring() {
        const selectedStructure =
            this.environmentObjects.structureSet?.selectedStructure;
        if (selectedStructure) {
            selectedStructure.undoContourChange();
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public redoContouring() {
        const selectedStructure =
            this.environmentObjects.structureSet?.selectedStructure;
        if (selectedStructure) {
            selectedStructure.redoContourChange();
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public canUndoContouring(): boolean {
        return (
            this.environmentObjects.structureSet?.selectedStructure?.canUndo() ??
            false
        );
    }

    public canRedoContouring(): boolean {
        return (
            this.environmentObjects.structureSet?.selectedStructure?.canRedo() ??
            false
        );
    }

    public setBlendImage(
        image: Image3D | undefined,
        registration?: RigidRegistration
    ) {
        if (!this.environmentObjects.primaryImage) {
            return;
        }
        if (this.environmentObjects.blendImage) {
            this.removeEnvironmentBlendImageInternal();
        }

        if (image) {
            if (
                !this.canBlend(
                    this.environmentObjects.primaryImage.parent,
                    image,
                    registration
                )
            ) {
                return;
            }
            this.environmentObjects.blendImage = ImageObject.fromImage3D(
                image,
                image.firstSlice
            );
            const mat = this.getFORTransformation(
                this.environmentObjects.primaryImage.parent.frameOfReferenceUid,
                image.frameOfReferenceUid,
                registration
            );
            if (!mat) {
                throw new Error(
                    'Could not construct transformation matrix for blending images.'
                );
            }
            this.environmentObjects.blendImage.transformation =
                new Transformation(mat);
            this.environmentObjects.blendImage.alpha = 0.5;
            this.environmentObjects.primaryImage.alpha = 0.5;
            ViewManager.resetViews();
        } else {
            this.environmentObjects.primaryImage.alpha = 1;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.primaryImage,
                    LayerBit.IMAGE_LAYER_BIT,
                    true
                )
            );
        }
        this._eventTarget.dispatchEvent(new Event('image_changed'));
        return this.environmentObjects;
    }

    public updatePrimaryImageAlpha(alpha: number) {
        if (
            this.environmentObjects.blendImage &&
            this.image &&
            alpha >= 0 &&
            alpha <= 1
        ) {
            this.image.alpha = alpha;
            this.environmentObjects.blendImage.alpha = 1 - alpha;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.image,
                    LayerBit.IMAGE_LAYER_BIT,
                    true
                )
            );
        }
    }

    private canBlend(
        image1: Image3D,
        image2: Image3D,
        registration?: RigidRegistration
    ): boolean {
        if (image1.frameOfReferenceUid === image2.frameOfReferenceUid) {
            return true;
        }
        return (
            this.getFORTransformation(
                image1.frameOfReferenceUid,
                image2.frameOfReferenceUid,
                registration
            ) !== null
        );
    }

    private getFORTransformation(
        primaryFOR: string,
        secondaryFOR: string,
        registration?: RigidRegistration
    ): mat4 | null {
        if (primaryFOR === secondaryFOR) {
            return mat4.create();
        }
        if (!registration) return null;
        if (
            primaryFOR === registration.sourceFrameOfReferenceUid &&
            secondaryFOR === registration.targetFrameOfReferenceUid
        ) {
            return registration.transformation;
        }
        if (
            primaryFOR === registration.targetFrameOfReferenceUid &&
            secondaryFOR === registration.sourceFrameOfReferenceUid
        ) {
            return mat4.invert(mat4.create(), registration.transformation);
        }
        return null;
    }

    private removeObject(obj: EnvironmentObject) {
        obj.delete();
    }

    public get structureSet(): StructureSetObject | undefined {
        return this.environmentObjects.structureSet;
    }

    public get dose(): DoseDistribution | undefined {
        return this.environmentObjects.dose;
    }
    public get image(): ImageObject | undefined {
        return this.environmentObjects.primaryImage;
    }

    public get blendImage(): ImageObject | undefined {
        return this.environmentObjects.blendImage;
    }

    public get getEnvironmentObjectIds(): EnvironmentObjectIds {
        const environmentObjectIds: Partial<EnvironmentObjectIds> = {};

        if (this.environmentObjects.primaryImage) {
            environmentObjectIds.primaryImage = {
                seriesInstanceUID:
                    this.environmentObjects.primaryImage.parent.seriesUid,
                studyInstanceUID:
                    this.environmentObjects.primaryImage.parent.studyUid,
            };
        }

        if (this.environmentObjects.blendImage) {
            environmentObjectIds.blendImage = {
                seriesInstanceUID:
                    this.environmentObjects.blendImage.parent.seriesUid,
                studyInstanceUID:
                    this.environmentObjects.blendImage.parent.studyUid,
            };
        }

        if (this.environmentObjects.structureSet) {
            environmentObjectIds.structureSet = {
                sopInstanceUID:
                    this.environmentObjects.structureSet.sopInstanceUID,
            };
        }

        if (this.environmentObjects.dose) {
            environmentObjectIds.dose = {
                sopInstanceUID: this.environmentObjects.dose.sopInstanceUID,
            };
        }

        return environmentObjectIds as EnvironmentObjectIds;
    }

    /** Update the selected structure and update views */
    public setSelectedStructure(selectedROI: RegionOfInterest | undefined) {
        if (!this.environmentObjects.structureSet) {
            return;
        }
        if (
            this.environmentObjects.structureSet.selectedStructure ===
            selectedROI
        ) {
            return;
        }
        this.environmentObjects.structureSet.selectedStructure = selectedROI;
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
        this.notifyStructureSetPropertyChangedListeners(
            this.environmentObjects.structureSet
        );
    }

    public setSelectedRoiByNumber(roiNumber: number) {
        if (!this.environmentObjects.structureSet) {
            return;
        }
        if (roiNumber === -1) {
            this.setSelectedStructure(undefined);
            return;
        }
        const roi = this.environmentObjects.structureSet.getRoi(roiNumber);
        if (roi) {
            this.setSelectedStructure(roi);
        }
    }

    public setRoisVisibility(visibility: boolean) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.setRoisVisibility(visibility);
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.structureSet,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
    }

    public getRoiVolumeCm3(roiNumber: number): number {
        return (
            this.environmentObjects.structureSet?.getRoi(roiNumber)
                ?.volumeCm3 ?? Number.NaN
        );
    }

    public updateImageObjectsWindowingLevel(level: WindowLevel): void {
        if (
            this.environmentObjects.primaryImage &&
            this.environmentObjects.primaryImage.windowLevel &&
            !this.environmentObjects.primaryImage.windowLevel.equals(level)
        ) {
            this.environmentObjects.primaryImage.windowLevel = level;
            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(
                    this.environmentObjects.primaryImage,
                    LayerBit.ALL_LAYERS,
                    true
                )
            );
            this._eventTarget.dispatchEvent(new Event('windowlevel_changed'));
        }
    }

    public handleInterpolateSingleSlice(
        roi: RegionOfInterest,
        coordinates: number
    ) {
        const contourOperations = new ContourOperations();
        if (!roi.texture) {
            return;
        }
        const sliceIndex = roi.texture?.patientCoordsToSdfIndices(
            vec3.fromValues(0, 0, coordinates)
        );

        contourOperations.handleInterpolateSingleSlice(roi, sliceIndex[2]);
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    public handleInterpolateAllSlices(roi: RegionOfInterest) {
        const contourOperations = new ContourOperations();
        contourOperations.handleInterpolateAllSlices(roi);
        ViewManager.viewUpdateEvent(
            new VolumeViewUpdateEvent(
                this.environmentObjects.structureSet,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }
}
